import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

const LanguageContext = createContext();

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

export const LanguageProvider = ({ children }) => {
    const { i18n } = useTranslation();
    const [currentLanguage, setCurrentLanguage] = useState((i18n.language || 'en').split('-')[0]);
    const navigate = useNavigate();
    const location = useLocation();

    const supportedLanguages = ['en', 'pt', 'es', 'fr', 'it', 'de'];

    useEffect(() => {
        // Se já tivermos uma escolha salva no localStorage, não fazemos nada
        // O LanguageWrapper trata da sincronização inicial pela URL

        // Mas podemos garantir que o estado local está sincronizado
        if (i18n.language) {
            setCurrentLanguage(i18n.language.split('-')[0]);
        }
    }, [i18n.language]);

    const changeLanguage = (lng) => {
        // Atualiza estado e storage
        i18n.changeLanguage(lng);
        localStorage.setItem('selectedLanguage', lng);
        setCurrentLanguage(lng);

        // Atualiza a URL para refletir a nova língua
        const currentPath = location.pathname;
        const currentSearch = location.search;

        const pathParts = currentPath.split('/');
        // pathParts[0] é "" (antes da primeira barra)
        // pathParts[1] deve ser a língua atual se estivermos numa rota válida

        if (pathParts.length > 1 && supportedLanguages.includes(pathParts[1])) {
            pathParts[1] = lng; // Substitui a língua
            const newPath = pathParts.join('/');
            navigate(newPath + currentSearch);
        } else {
            // Se por acaso estivermos numa rota sem língua (ex: raiz absoluta ou erro), injeta a língua
            // Cuidado para não duplicar barras //
            const cleanPath = currentPath === '/' ? '' : currentPath;
            navigate(`/${lng}${cleanPath}${currentSearch}`);
        }
    };

    const languages = [
        { code: 'en', name: 'English', flag: '🇬🇧' },
        { code: 'es', name: 'Español', flag: '🇪🇸' },
        { code: 'it', name: 'Italiano', flag: '🇮🇹' },
        { code: 'fr', name: 'Français', flag: '🇫🇷' },
        { code: 'pt', name: 'Português', flag: '🇵🇹' },
        { code: 'de', name: 'Deutsch', flag: '🇩🇪' }
    ];

    const value = {
        currentLanguage,
        changeLanguage,
        languages,
        t: i18n.t
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};
